import { ImageName } from "@mcleod/images";
import {
    Button, ButtonVariant, Checkbox, ClickEvent, Component, CrudDecorator, DataSourceMode, Image, Label, LabelProps,
    Layout, MultiButton, Panel, Switch, TableRow, Textbox
} from "@mcleod/components";
import { Alignment, Currency, CurrencySettings, DisplayType, DisplayValue, ModelRow, StringUtil } from "@mcleod/core";
import { CommonTooltips } from "@mcleod/common";
import { OrderPayablesGroupByTable } from "./OrderPayablesGroupByTable";
import { PayablesLookup } from "./OrderPayables";
import { RowOrderPayablesProfile } from "./models/ModelOrderPayablesProfile";
import { DriverSettlementGroupByTable } from "./driver-settlement/DriverSettlementGroupByTable";
import { RowDriverSettlementProfile } from "../driver-settlement/src/models/ModelDriverSettlementProfile";


export interface DisplayDetailsProps {
    tableRow: TableRow;
    profileRow: RowOrderPayablesProfile | RowDriverSettlementProfile;
}
export class OrderPayablesUtil {
    static setTooltipContent(component: Component, modelRow: ModelRow) {
        switch (component.id) {
            case "labelFactoringCompany": {
                CommonTooltips.setTooltipContentLayout(component, modelRow.get("factoring_company_id"), "lme/settlement/FactoringCompanyQuickInfo");
                break;
            }
            case "labelOrderId": {
                if (modelRow.isNull("movement_id")) {
                    modelRow.set("orders.id", modelRow.get("order_id"));
                    CommonTooltips.setTooltipContentLayout(component, modelRow, "lme/settlement/OrderPayablesOrderIdQuickInfo");
                }
                else {
                    CommonTooltips.setTooltipContentLayout(component, modelRow.get("movement_id"), "lme/settlement/OrderPayablesOrderIdQuickInfo");
                }
                break;
            }
            case "labelBackOfficeReasonDescr": {
                CommonTooltips.setTooltipContentLayout(component, modelRow.get("back_office_reason_code"), "lme/settlement/BackOfficeReasonCodeQuickInfo");
                break;
            }
            case "textboxDriverName": {
                CommonTooltips.setTooltipContentLayout(component, modelRow.get("driver_id"), "lme/dispatch/asset/DriverQuickInfo");
                break;
            }
            case "labelPayeeName": {
                CommonTooltips.setTooltipContentLayout(component, modelRow.get("payee_id"), "lme/settlement/SettlementPayeeQuickInfo");
                break;
            }
            default: {
                if (component instanceof Label && component.displayType === DisplayType.STRING) {
                    component.tooltip = modelRow.get(component.field);
                }
            }
        }
    }

    static setComponentDetails(props: DisplayDetailsProps) {
        const imageSettlementStatus = props.tableRow.findComponentById("imageSettlementStatus") as Image;
        if (imageSettlementStatus != null) {
            const isProcess: boolean = props.tableRow.data.get("ready_to_pay_flag") == "Y";
            imageSettlementStatus.name = isProcess ? ImageName.settlementProcess : ImageName.settlementHold;
            imageSettlementStatus.color = isProcess ? "success" : "error";
        }

        const labelOriginState = props.tableRow.findComponentById("labelOriginState") as Label;
        if (labelOriginState != null && !props.tableRow.data.isNull("origin_state")) {
            labelOriginState.text = ", " + props.tableRow.data.get("origin_state");
        }

        const labelDestinationState = props.tableRow.findComponentById("labelDestinationState") as Label;
        if (labelDestinationState != null && !props.tableRow.data.isNull("destination_state")) {
            labelDestinationState.text = ", " + props.tableRow.data.get("destination_state");
        }

        const labelAge = props.tableRow.findComponentById("labelAge") as Label;
        if (labelAge != null) {
            const age = props.tableRow.data.getNumber("age");
            labelAge.text = age == 1 ? `${age} Day` : `${age} Days`;
        }
        const recordType = props.tableRow.data.get("record_type");
        const imageRecordType = props.tableRow.findComponentById("imageRecordType") as Image;
        if (imageRecordType != null) {
            imageRecordType.name = recordType != "S" ? ImageName.payAdjustment : null;
            imageRecordType.visible = recordType != "S";
        }
        if (props.tableRow.data.get("record_type") == "P") {
            const rowCurrencyCode = (props.tableRow.data as ModelRow).getCurrency("pay_amount")?.getCurrencyCode();
            if (!props.profileRow.isNull("currency_type") && rowCurrencyCode != props.profileRow.get("currency_type")) {
                props.tableRow.enabled = false;
                const checkBox = props.tableRow.findComponentById("checkboxSelectRow") as Checkbox;
                if (checkBox) {
                    checkBox.enabled = false;
                    checkBox.tooltip = "The currency of this record does not match the filter currency.";
                }
            }
            const switchLoaded = props.tableRow.findComponentById("switchLoaded") as Switch;
            if (switchLoaded != null) {
                switchLoaded.visible = false;
            }
        }

        const imageFrozenPayables = props.tableRow.findComponentById("imageFrozenPayables") as Image;
        if (imageFrozenPayables != null) {
            imageFrozenPayables.visible = !props.tableRow.data.isNull("freeze_by");
        }

        const textboxDeliveryDate = props.tableRow.findComponentById("textboxDeliveryDate") as Textbox;
        if (textboxDeliveryDate != null) {
            textboxDeliveryDate.tooltip = DisplayValue.getDateDisplayValue(props.tableRow.data.get("delivery_date"), DisplayType.DATETIME);
        }

        if(props.tableRow.data.get("triumphpay_active")) {
            const statusBadge = props.tableRow.findComponentById("labelStatusBadge") as Label;
            if (!statusBadge) {
                return;
            }
            if (!props.tableRow.data.isNull("submittal_exception")) {
                this.configureExceptionStatus(statusBadge, props.tableRow.data);
            } else if (props.tableRow.data.get("ready_to_submit")) {
                this.configureReadyStatus(statusBadge);
            } else if (props.tableRow.data.get("triumphpay_submitted")) {
                this.configureSubmittedStatus(statusBadge, props.tableRow.data);
            } else if (props.tableRow.data.get("paid")) {
                this.configurePaidStatus(statusBadge, props.tableRow.data);
            }else{
                statusBadge.visible = false;
            }
        }
    }

    private static configureExceptionStatus(statusBadge: Label, data: ModelRow): void {
        statusBadge.text = "Exception";
        statusBadge.color = "error";
        statusBadge.borderColor = "error";
        statusBadge.visible = true;

        if(!StringUtil.isEmptyString(data.get("submittal_exception"))) {
            const tooltipPanel = new Panel();
            tooltipPanel.add(new Label({ text: "Triumph Exception", imageName: ImageName.circleError, color: "error", imageColor: "error", rowBreak: true}));
            tooltipPanel.add(new Label({ text: data.get("submittal_exception").split('\n').map(line => "• " + line).join('\n'), rowBreak: false }));
            statusBadge.tooltipDynamicContent = tooltipPanel;
        }
    }

    private static configureReadyStatus(statusBadge: Label): void {
        statusBadge.visible = true;
        statusBadge.tooltip = "Ready to Submit";
        statusBadge.borderColor = "primary";
        statusBadge.text = "Ready";
        statusBadge.color = "primary";
        const tooltipPanel = new Panel();
        tooltipPanel.add(new Label({ text: "Triumph Ready to Submit", imageName: ImageName.circleCheckEmpty, color: "primary", imageColor: "primary", rowBreak: true}));
        statusBadge.tooltipDynamicContent = tooltipPanel;
    }

    private static configureSubmittedStatus(statusBadge: Label, data: ModelRow): void {
        statusBadge.visible = true;
        statusBadge.text = "Submitted";
        statusBadge.color = "success";
        statusBadge.borderColor = "success";
        const panel = this.setupTooltipPanel(data, true);
        statusBadge.tooltipDynamicContent = panel;
    }

    private static configurePaidStatus(statusBadge: Label, data: ModelRow): void {
        statusBadge.visible = true;
        statusBadge.text = "Paid";
        statusBadge.backgroundColor = "success";
        statusBadge.borderColor = "success";
        statusBadge.color = "primary.reverse";
        const panel = this.setupTooltipPanel(data, false);
        statusBadge.tooltipDynamicContent = panel;
    }


    private static setupTooltipPanel(modelRow: any, isSubmittedBadge: boolean): Panel {
        const statusLabelProps: Partial<LabelProps> = {
            color: isSubmittedBadge ? "warning" : "success",
            imageName: isSubmittedBadge ? ImageName.clock : ImageName.circleCheckEmpty,
            text: isSubmittedBadge ? "Triumph Pending" : "Triumph Paid",
            fontBold: true,
            rowBreak: true
        };

        const contentPanel = new Panel({ rowBreak: false });
        contentPanel.add(new Label(statusLabelProps));

        const addLabelIfNotEmpty = (fieldKey: string, prefix: string, rowBreak: boolean) => {
            const value = modelRow.get(fieldKey);
            if (!StringUtil.isEmptyString(value)) {
                contentPanel.add(new Label({
                    text: `${prefix}: ${value}`,
                    rowBreak,
                    marginLeft: 10
                }));
            }
        };

        if (isSubmittedBadge) {
            addLabelIfNotEmpty("triumphpay_payment_id", "Payment ID", true);
        } else {
            addLabelIfNotEmpty("triumphpay_transaction_date", "Payment Date", true);
        }
        addLabelIfNotEmpty("triumphpay_process_date", "Submitted On", false);

        const tooltipPanel = new Panel();

        tooltipPanel.add(contentPanel);
        tooltipPanel.add(this.getTriumphPayPortalButton());

        return tooltipPanel;
    }

    private static getTriumphPayPortalButton(): Button {
        return new Button({
            imageName: ImageName.detail,
            variant: ButtonVariant.round,
            tooltip: "Open TriumphPay Site",
            color: "primary",
            link: "https://secure.triumphpay.com/Login?ReturnUrl\u003d%2f",
            marginRight: 4,
            fillHeight: true
        })
    }

    static async setPayAdjustmentInfo(tableRow: TableRow, layoutGroupBy: OrderPayablesGroupByTable | DriverSettlementGroupByTable,
        payablesLookupMap: PayablesLookup, payAmountField: string = "pay_amount") {

        const modelRow = tableRow.data as ModelRow;
        const textboxPayAdjustment = tableRow.findComponentById("textboxPayAdjustment") as Textbox;
        const textboxSettlementTotal = tableRow.findComponentById("textboxSettlementTotal") as Textbox;
        const labelTotalPay = tableRow.findComponentById("labelTotalPay") as Label;
        const payAmount = modelRow.getCurrency(payAmountField);
        const adjRows = this.getRelatedPayAdjustmentRows(modelRow, payablesLookupMap);
        if (modelRow.get("record_type") === "S") {
            await OrderPayablesUtil.getSettlementAdjustmentInfo(modelRow, payablesLookupMap, layoutGroupBy).then(async (result) => {
                modelRow.set("pay_adjustment_total", result.payAdjustmentTotal);
                await payAmount.add(result.payAdjustmentTotal);
                modelRow.set("settlement_total", payAmount);
            });
        } else {
            modelRow.set("pay_adjustment_total", modelRow.getCurrency(payAmountField));
            modelRow.set("settlement_total", payAmount);
        }

        if (textboxPayAdjustment && textboxSettlementTotal && modelRow.get("record_type") === "S") {
            const numDeductions = modelRow.getNumber("num_deductions", 0);
            if (numDeductions > 0) {
                textboxPayAdjustment.style.textDecoration = "underline";
                this.setAdjustmentTooltip(adjRows, tableRow, textboxPayAdjustment, layoutGroupBy);
            }
            const imageWarning = tableRow.findComponentById("imageAdjustmentWarning") as Image;
            if (imageWarning) {
                const groupByMatches: boolean = this.checkGroupByMatches(modelRow, layoutGroupBy);

                imageWarning.visible = this.hasAdjustmentWithDifferentStatus(modelRow, adjRows, layoutGroupBy) || numDeductions != adjRows.length || !groupByMatches;
            }
            textboxPayAdjustment.displayComponentData(modelRow, null, tableRow.index);
            textboxSettlementTotal.displayComponentData(modelRow, null, tableRow.index);
            //display label in case amount has changed from allocation calculation
            if (labelTotalPay)  {
                labelTotalPay.displayComponentData(modelRow, null, tableRow.index);
            }
        } else if (textboxPayAdjustment && textboxSettlementTotal && modelRow.get("record_type") === "P") {
            //Only have the tooltip on records with an order or move
            if (!modelRow.isNull("order_id") && !modelRow.isNull("movement_id")) {
                textboxPayAdjustment.style.textDecoration = "underline";
                this.setAdjustmentTooltip(adjRows, tableRow, textboxPayAdjustment, layoutGroupBy);
            }
            const currencyCode = modelRow.getCurrency(payAmountField)?.getCurrencyCode() ?? CurrencySettings.getSingleton().getTransactionalCurrency();
            labelTotalPay.text = new Currency({ amount: 0, currency_code: currencyCode }).toString();
            labelTotalPay.color = "default";
            textboxPayAdjustment.displayComponentData(modelRow, null, tableRow.index);
            textboxSettlementTotal.displayComponentData(modelRow, null, tableRow.index);
        }

    }

    static setAdjustmentTooltip(adjRows: ModelRow[], tableRow: TableRow, textboxPayAdjustment: Textbox, layoutGroupBy: OrderPayablesGroupByTable | DriverSettlementGroupByTable) {
        const modelRow = tableRow.data as ModelRow;
        CommonTooltips.setTooltipContentLayout(textboxPayAdjustment, null, "lme/settlement/DrsPendingDeductList", { maxWidth: 900 }, {
            movement_id: modelRow.get("movement_id"),
            order_id: modelRow.get("order_id"),
            payee_id: modelRow.get("payee_id"),
            in_filtered_records: adjRows.filter((row) => {
                const groupByMatches: boolean = this.checkGroupByMatches(row, layoutGroupBy);
                return this.matchingStatus(row, modelRow) && this.matchingCurrency(row, modelRow) && groupByMatches;
            }).map(row => row.get("record_id")).join(",")
        });
        textboxPayAdjustment.tooltipDynamicOptions = { position: Alignment.LEFT };
    }

    public static getRelatedPayAdjustmentRows(settlementRow: ModelRow, payablesLookupMap: PayablesLookup): ModelRow[] {
        const adjustments: ModelRow[] = [];
        const key = PayablesLookup.key(settlementRow);
        for (const [, adjustment] of payablesLookupMap.getAdjustmentsWithKey(key)) {
            if (this.matchingPayable(adjustment, settlementRow)) {
                adjustments.push(adjustment);
            }
        }
        return adjustments;
    }

    public static async getSettlementAdjustmentInfo(settlementRow: ModelRow, payablesLookupMap: PayablesLookup, layoutGroupBy: OrderPayablesGroupByTable | DriverSettlementGroupByTable): Promise<{
        payAdjustmentTotal: Currency,
        adjustments: ModelRow[]
    }> {
        const payAdjustmentTotal = new Currency({currency_code: layoutGroupBy.getProfileFilter().getFilterCurrencyType(), amount: 0});
        const adjustments: ModelRow[] = [];
        const key = PayablesLookup.key(settlementRow);
        for (const [_id, adjustment] of payablesLookupMap.getAdjustmentsWithKey(key)) {
            const statusMatches = this.matchingStatus(adjustment, settlementRow);
            const currencyMatches = this.matchingCurrency(adjustment, settlementRow);
            const groupByMatches: boolean = this.checkGroupByMatches(adjustment, layoutGroupBy);

            if (this.matchingPayable(adjustment, settlementRow) && statusMatches && adjustment.get("record_type") === "P" && groupByMatches && currencyMatches) {
                await payAdjustmentTotal.add(adjustment.getCurrency("pay_amount"));
            }
            if (this.matchingPayable(adjustment, settlementRow) && adjustment.get("record_type") == "P") {
                adjustments.push(adjustment);
            }
        }

        return { payAdjustmentTotal, adjustments };
    }

    public static hasAdjustmentWithDifferentStatus(settlementRow: ModelRow, data: ModelRow[], layoutGroupBy: OrderPayablesGroupByTable | DriverSettlementGroupByTable): boolean {
        for (const dataRow of data) {
            const statusMatches = this.matchingStatus(dataRow, settlementRow);
            const currencyCodeMatches = this.matchingCurrency(dataRow, settlementRow);
            const groupByMatches: boolean = this.checkGroupByMatches(dataRow, layoutGroupBy);

            if (!statusMatches || !groupByMatches || !currencyCodeMatches) {
                return true;
            }
        }
        return false;
    }

    public static matchingStatus(row1: ModelRow, row2: ModelRow): boolean {
        const statusMatches = row1.get("ready_to_pay_flag") === row2.get("ready_to_pay_flag");
        const holdReasonMatches = row1.get("back_office_reason_code") === row2.get("back_office_reason_code");
        return statusMatches && holdReasonMatches;
    }

    public static matchingPayable(row1: ModelRow, row2: ModelRow): boolean {
        if (row1 == null || row2 == null) return false;
        const orderIdMatches = row1.get("order_id") === row2.get("order_id");
        const movementIdMatches = row1.get("movement_id") === row2.get("movement_id");
        const payeeIdMatches = row1.get("payee_id") === row2.get("payee_id");
        return orderIdMatches && movementIdMatches && payeeIdMatches;
    }

    public static matchingCurrency(row1:ModelRow, row2: ModelRow): boolean{
        return row1.getCurrency("pay_amount")?.currency_code == row2.getCurrency("pay_amount")?.currency_code;
    }

    static settlementInTabWithAdjustment(modelRow: ModelRow, payablesLookupMap: PayablesLookup): ModelRow {
        const key = PayablesLookup.key(modelRow);
        const settlement = payablesLookupMap.getSettlementWithKey(key);
        if (settlement == null) return null;

        const orderIdMatches = settlement.get("order_id") === modelRow.get("order_id");
        const movementIdMatches = settlement.get("movement_id") === modelRow.get("movement_id");
        const payeeIdMatches = settlement.get("payee_id") === modelRow.get("payee_id");
        const statusMatches = settlement.get("ready_to_pay_flag") === modelRow.get("ready_to_pay_flag");
        const holdReasonMatches = settlement.get("back_office_reason_code") === modelRow.get("back_office_reason_code");
        const currencyMatches = this.matchingCurrency(settlement, modelRow);
        if (orderIdMatches && movementIdMatches && payeeIdMatches && statusMatches && holdReasonMatches && currencyMatches && settlement.get("record_type") === "S") {
            return settlement;
        }

        return null;
    }

    static generateAddButton() {
        const multiButton = new MultiButton({
            id: "addButton",
            rowBreak: false,
            color: "primary.reverse",
            backgroundColor: "primary",
            imageName: ImageName.add,
            width: 170,
            marginRight: 25
        });
        multiButton.primaryButton.setProps({
            id: "buttonAddSettlement",
            caption: "Add Settlement",
            onClick: (event) => OrderPayablesUtil.buttonAddOrderPayableOnClick(event, "lme/settlement/Settlement")
        });
        multiButton.dropdownItems = [
            {
                text: "Add Pay Adjustment",
                onClick: (event) => OrderPayablesUtil.buttonAddOrderPayableOnClick(event, "lme/settlement/PendingPayAdjustment")
            }
        ];
        return multiButton;
    }

    private static buttonAddOrderPayableOnClick(event: ClickEvent, layoutName: string) {
        const layout = Layout.getLayout(layoutName);
        const crudPanel = new CrudDecorator({
            layout: layout,
            mode: DataSourceMode.ADD,
            headerProps: {
                showClose: true,
                showSaveAndClose: true
            }
        });
        crudPanel.slideIn({ speed: 200 });
    }

    private static checkGroupByMatches(modelRow: ModelRow, layoutGroupBy: OrderPayablesGroupByTable | DriverSettlementGroupByTable): boolean {
        const groupByTable = layoutGroupBy?.tableGroupBy;
        if (layoutGroupBy instanceof OrderPayablesGroupByTable) {
            return groupByTable?.selectedRows?.length === 0 || groupByTable?.selectedRows.some((tableRow: TableRow) => tableRow.data.get("group_by_value") == modelRow.get(layoutGroupBy.getGroupByFieldName()));
        } else if (layoutGroupBy instanceof DriverSettlementGroupByTable) {
            return groupByTable?.selectedRow?.data.get("payee_id") == modelRow.get("payee_id");
        }
        return false;
    }
}
